import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fundReceipts, type FundDebtResult } from '@/api/fund_receipts';
import { orgs } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const now = new Date();
const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface RowState {
  sick_calculation_id: string | null;
  employee_id: string | null;
  amount: string;
  // Підписи (з fund-debt або '—' для рядків збереженого документа).
  label?: string;
  accrued?: string;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  comment: string;
  items: RowState[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: now.toISOString().slice(0, 16),
  period_year: now.getFullYear(),
  period_month: now.getMonth() + 1,
  comment: '',
  items: [],
};

export default function FundReceiptEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  // ч.880: prefill з «Повʼязаних дій» лікарняного (?org=&year=&month=).
  const [searchParams] = useSearchParams();
  const prefillOrg = searchParams.get('org');
  const prefillYear = Number(searchParams.get('year') || '');
  const prefillMonth = Number(searchParams.get('month') || '');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');

  const docQuery = useQuery({
    queryKey: ['fund-receipt', id],
    queryFn: () => fundReceipts.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'fund-receipt', (cid) => fundReceipts.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);
  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  // Лікарняні з невідшкодованим боргом Фонду за період — для «На основі нарахування».
  const debtQ = useQuery<FundDebtResult>({
    queryKey: ['fund-debt', form.organization_id, form.period_year, form.period_month],
    queryFn: () => fundReceipts.fundDebt({
      organization_id: form.organization_id,
      period_year: form.period_year, period_month: form.period_month,
    }),
    enabled: !!form.organization_id && !isPosted,
  });
  // Ще не додані у рядки документа.
  const available = useMemo(() => {
    const used = new Set(form.items.map((r) => r.sick_calculation_id));
    return (debtQ.data?.items ?? []).filter((d) => !used.has(d.sick_calculation_id));
  }, [debtQ.data, form.items]);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = prefillOrg || availableOrgs[0]?.id || '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        period_year: prefillYear || f.period_year,
        period_month: prefillMonth || f.period_month,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        period_year: d.period_year,
        period_month: d.period_month,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          sick_calculation_id: it.sick_calculation_id,
          employee_id: it.employee_id,
          amount: String(it.amount),
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  // Підписи рядків збереженого документа — з fund-debt того ж періоду
  // (шукаємо по sick_calculation_id; вже відшкодовані там відсутні → лишаємо '—').
  const labelOf = useMemo(() => {
    const m = new Map<string, { label: string; accrued: string }>();
    (debtQ.data?.items ?? []).forEach((d) => m.set(d.sick_calculation_id, {
      label: `${d.document_number ?? ''} — ${d.employee_name ?? ''}`,
      accrued: String(d.accrued ?? ''),
    }));
    return m;
  }, [debtQ.data]);

  function addFromAccrual(calcId: string) {
    const d = debtQ.data?.items.find((x) => x.sick_calculation_id === calcId);
    if (!d) return;
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        sick_calculation_id: d.sick_calculation_id,
        employee_id: d.employee_id,
        amount: String(d.amount),  // залишок до відшкодування
        label: `${d.document_number ?? ''} — ${d.employee_name ?? ''}`,
        accrued: String(d.accrued ?? ''),
      }],
    }));
  }
  function updRow(i: number, amount: string) {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], amount } as RowState;
      return { ...f, items };
    });
  }
  function removeRow(i: number) {
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  }

  const total = useMemo(
    () => form.items.reduce((s, it) => s + Number(it.amount || 0), 0),
    [form.items]);

  // Update-схема backend'а — extra=forbid БЕЗ organization_id/fund_marker
  // (незмінні після створення) → на PATCH їх НЕ шлемо, інакше 422.
  function payload() {
    return {
      document_number: form.document_number || undefined,
      document_date: form.document_date,
      period_year: form.period_year,
      period_month: form.period_month,
      comment: form.comment || null,
      items: form.items
        .filter((it) => Number(it.amount) > 0)
        .map((it) => ({
          sick_calculation_id: it.sick_calculation_id,
          employee_id: it.employee_id,
          amount: it.amount,
        })),
    };
  }
  const createPayload = () => ({
    organization_id: form.organization_id, fund_marker: 'PFU', ...payload(),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return fundReceipts.create(createPayload());
      return fundReceipts.update(id!, payload());
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['fund-receipts'] });
      if (isNew) { qc.setQueryData(['fund-receipt', doc.id], doc); navigate(`/fund-receipts/${doc.id}`); }
      else qc.invalidateQueries({ queryKey: ['fund-receipt', id] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });

  const saveAndPost = useMutation({
    mutationFn: async () => {
      const doc = isNew
        ? await fundReceipts.create(createPayload())
        : await fundReceipts.update(id!, payload());
      try {
        await fundReceipts.post(doc.id);
      } catch (e) {
        // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
        if (isNew) navigate(`/fund-receipts/${doc.id}`, { replace: true });
        throw e;
      }
      return fundReceipts.get(doc.id);
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['fund-receipts'] });
      qc.setQueryData(['fund-receipt', doc.id], doc);
      navigate(`/fund-receipts/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });

  const unpost = useMutation({
    mutationFn: async () => {
      const fresh = await fundReceipts.unpost(id!);
      qc.setQueryData(['fund-receipt', id], fresh);
      return fresh;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fund-receipts'] }),
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });

  const remove = useMutation({
    mutationFn: () => fundReceipts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fund-receipts'] });
      navigate('/fund-receipts');
    },
  });

  const canSave = !!form.organization_id && !!form.document_date && !isPosted;

  // ч.889 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'items', title: 'Відшкодування Фонду', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове надходження від Фонду' : `Надходження ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.fund_receipts', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          isNew={isNew}
          onBackToList={() => navigate('/fund-receipts')}
          onSave={() => save.mutate()}
          saveDisabled={!canSave}
          savePending={save.isPending || saveAndPost.isPending}
          isPosted={isPosted}
          onPost={() => saveAndPost.mutate()}
          onUnpost={() => unpost.mutate()}
          postPending={saveAndPost.isPending}
          unpostPending={unpost.isPending}
          deletable={!isNew}
          onDelete={() => { if (confirm('Видалити документ?')) remove.mutate(); }}
          deletePending={remove.isPending}
        />
        </div>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Гроші Фонду (ПФУ) по лікарняних на спецрахунок. Оберіть нарахування
        лікарняного — рядок заповниться залишком до відшкодування.
      </p>

      {isPosted && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>
          ✓ проведено.
        </div>
      )}

      {error && (
        <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/fund-receipts/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.fund_receipts', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value, items: [] })}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 160 }}>
          <label>Дата *</label>
          <DatePopover value={form.document_date.slice(0, 10)}
            onChange={(v) => setForm({ ...form, document_date: `${v}T00:00` })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={form.period_month} disabled={isPosted}
              onChange={(e) => setForm({ ...form, period_month: Number(e.target.value) })}
              style={{ flex: 2 }}>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={form.period_year} disabled={isPosted}
              onChange={(e) => setForm({ ...form, period_year: Number(e.target.value) })}
              style={{ flex: 1 }}>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Відшкодування Фонду">
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {!isPosted && (
          <div className="field" style={{ minWidth: 340 }}>
            <label>На основі нарахування (лікарняні з боргом Фонду)</label>
            <select value=""
              onChange={(e) => { if (e.target.value) addFromAccrual(e.target.value); }}
              disabled={!form.organization_id}>
              <option value="">
                {!form.organization_id ? '— спершу оберіть організацію —'
                  : debtQ.isFetching ? '— завантаження…'
                  : available.length === 0
                    ? '— немає невідшкодованих лікарняних за період —'
                    : '— оберіть лікарняний (додасться рядком) —'}
              </option>
              {available.map((d) => (
                <option key={d.sick_calculation_id} value={d.sick_calculation_id}>
                  {d.document_number} — {d.employee_name} — залишок {fmt2(d.amount)} грн
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Рядки */}
      <TchTable>
        <thead>
          <tr>
            <th>Нарахування лікарняного</th>
            <th style={{ width: 140, textAlign: 'right' }}>Нараховано (ПФУ)</th>
            <th style={{ width: 140, textAlign: 'right' }}>Сума надходження</th>
            <th style={{ width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const info = it.label
              ? { label: it.label, accrued: it.accrued ?? '' }
              : labelOf.get(it.sick_calculation_id ?? '') ?? { label: '—', accrued: '' };
            return (
              <tr key={i}>
                <td>{info.label}</td>
                <td style={{ textAlign: 'right' }}>
                  {info.accrued ? fmt2(info.accrued) : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {isPosted ? fmt2(it.amount) : (
                    <input type="number" step="0.01" value={it.amount}
                      style={{ width: '100%', textAlign: 'right' }}
                      onChange={(e) => updRow(i, e.target.value)} />
                  )}
                </td>
                <td>
                  {!isPosted && (
                    <button className="danger" style={{ padding: '0.2rem 0.5rem' }}
                      onClick={() => removeRow(i)}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} style={{ fontWeight: 600 }}>Разом</td>
            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt2(total)}</td>
            <td />
          </tr>
        </tfoot>
      </TchTable>

      {form.items.length === 0 && (
        <p style={{ color: 'var(--muted)', margin: '0.5rem 0 0' }}>
          Оберіть нарахування лікарняного вгорі — воно додасться рядком із
          залишком до відшкодування.
        </p>
      )}
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.fund_receipts" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
